import { TypedDocumentNode, gql } from '@apollo/client';
import { faRepeat, faStore } from '@fortawesome/pro-solid-svg-icons';
import { FormattedList, FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import Button from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM, Text16, Title3 } from '@sorare/core/src/atoms/typography';
import Bold from '@sorare/core/src/atoms/typography/Bold';
import Dialog from '@sorare/core/src/components/dialog';
import { groupBy } from '@sorare/core/src/lib/arrays';
import {
  isMyCardListedOnMarket,
  isSentInDirectOffer,
} from '@sorare/core/src/lib/cards';
import { glossary } from '@sorare/core/src/lib/glossary';
import { formatLineupDisplayName } from '@sorare/core/src/lib/so5';

import DivisionLogo from 'components/so5/DivisionLogo';

import { WarningRow } from './WarningRow';
import { WarningDialog_anyCard } from './__generated__/index.graphql';

const messages = defineMessages({
  dialogTitle: {
    id: 'UsedCardsDialog.dialogTitle',
    defaultMessage: 'Warning',
  },
  warningTitle: {
    id: 'ConfirmWarningDialog.warningTitle',
    defaultMessage:
      'You selected {cards, plural, one {a card} other {cards}} that {warnings}',
  },
  usedCards: {
    id: 'ConfirmWarningDialog.warningTitle.usedCards',
    defaultMessage:
      '{cards, plural, one {is in a registered lineup} other {are in registered lineups}}',
  },
  sentInLiveOfferCards: {
    id: 'ConfirmWarningDialog.warningTitle.sentInLiveOfferCards',
    defaultMessage:
      '{cards, plural, one {is sent is a live offer} other {are sent in live offers}}',
  },
  listedCards: {
    id: 'ConfirmWarningDialog.warningTitle.listedCards',
    defaultMessage:
      '{cards, plural, one {is listed in the Market} other {are listed in the Market}}',
  },
  usedInAnotherLineupWarning: {
    id: 'UsedCardsDialog.usedInAnotherLineupWarning',
    defaultMessage:
      'Selecting this player will unregister upcoming <b>{lineupDisplayName}</b> lineup',
  },
  listedWarning: {
    id: 'UsedCardsDialog.listedWarning',
    defaultMessage:
      'Selecting this player will <b>unlist</b> your card from the Market',
  },
  sentInLiveOfferWarning: {
    id: 'UsedCardsDialog.sentInLiveOfferWarning',
    defaultMessage:
      'Selecting this player will <b>cancel</b> ongoing trade offer(s) including this card',
  },
});

const Body = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--triple-unit);
`;
const Buttons = styled.div`
  display: flex;
  gap: var(--unit);
`;
const WarningRowList = styled(Vertical).attrs({ gap: 2 })``;
const DivisionLogoWrapper = styled.div`
  width: var(--triple-unit);
`;

type Props = {
  open: boolean;
  onCancel: () => void;
  onContinue: () => void;
  cards: WarningDialog_anyCard[];
  so5FixtureSlug: string;
  so5LineupId: string;
};

export const WarningDialog = ({
  open,
  onCancel,
  onContinue,
  cards,
  so5FixtureSlug,
  so5LineupId,
}: Props) => {
  const listedOnMarketCards = cards.filter(card =>
    isMyCardListedOnMarket(card)
  );
  const sentInDirectOfferCards = cards.filter(card =>
    isSentInDirectOffer(card)
  );
  const usedInOtherLineupCards = cards
    .map(anyCard => {
      const otherLineup = anyCard.openedSo5Lineups.find(
        l => l.so5Fixture.slug === so5FixtureSlug && l.id !== so5LineupId
      );

      return otherLineup ? { anyCard, otherLineup } : null;
    })
    .filter(Boolean);

  const warningCardsCount =
    listedOnMarketCards.length +
    sentInDirectOfferCards.length +
    usedInOtherLineupCards.length;

  if (!warningCardsCount) {
    return null;
  }

  return (
    <Dialog
      maxWidth="sm"
      open={open}
      title={
        <Text16 bold className="text-center">
          <FormattedMessage {...messages.dialogTitle} />
        </Text16>
      }
      onClose={onCancel}
    >
      <Body>
        {warningCardsCount > 0 && (
          <>
            <Title3>
              <FormattedMessage
                {...messages.warningTitle}
                values={{
                  cards: warningCardsCount,
                  warnings: (
                    <FormattedList
                      type="disjunction"
                      value={[
                        usedInOtherLineupCards.length > 0 && (
                          <FormattedMessage
                            key="used"
                            {...messages.usedCards}
                            values={{
                              cards: warningCardsCount,
                            }}
                          />
                        ),
                        sentInDirectOfferCards.length > 0 && (
                          <FormattedMessage
                            key="sentInLiveOffer"
                            {...messages.sentInLiveOfferCards}
                            values={{
                              cards: warningCardsCount,
                            }}
                          />
                        ),
                        listedOnMarketCards.length > 0 && (
                          <FormattedMessage
                            key="list"
                            {...messages.listedCards}
                            values={{
                              cards: warningCardsCount,
                            }}
                          />
                        ),
                      ].filter(Boolean)}
                    />
                  ),
                }}
              />
            </Title3>
            <WarningRowList>
              {usedInOtherLineupCards.map(({ anyCard, otherLineup }) => (
                <WarningRow
                  key={anyCard.slug}
                  card={anyCard}
                  warning={
                    <>
                      {otherLineup.so5Leaderboard && (
                        <DivisionLogoWrapper>
                          <DivisionLogo
                            so5Leaderboard={otherLineup.so5Leaderboard}
                          />
                        </DivisionLogoWrapper>
                      )}
                      <LabelM>
                        <FormattedMessage
                          {...messages.usedInAnotherLineupWarning}
                          values={{
                            lineupDisplayName:
                              formatLineupDisplayName(otherLineup),
                            b: Bold,
                          }}
                        />
                      </LabelM>
                    </>
                  }
                />
              ))}
              <>
                {Object.values(
                  groupBy(
                    [...listedOnMarketCards, ...sentInDirectOfferCards],
                    card => card.slug
                  )
                )
                  .filter(Boolean)
                  .map(([anyCard]) => (
                    <WarningRow
                      key={anyCard.slug}
                      card={anyCard}
                      warningLevel="error"
                      warning={
                        <div>
                          {listedOnMarketCards.some(
                            c => c.slug === anyCard.slug
                          ) && (
                            <Horizontal>
                              <FontAwesomeIcon icon={faStore} size="sm" />
                              <LabelM>
                                <FormattedMessage
                                  {...messages.listedWarning}
                                  values={{
                                    b: Bold,
                                  }}
                                />
                              </LabelM>
                            </Horizontal>
                          )}
                          {sentInDirectOfferCards.some(
                            c => c.slug === anyCard.slug
                          ) && (
                            <Horizontal>
                              <FontAwesomeIcon icon={faRepeat} size="sm" />
                              <LabelM>
                                <FormattedMessage
                                  {...messages.sentInLiveOfferWarning}
                                  values={{
                                    b: Bold,
                                  }}
                                />
                              </LabelM>
                            </Horizontal>
                          )}
                        </div>
                      }
                    />
                  ))}
              </>
            </WarningRowList>
          </>
        )}
        <Buttons>
          <Button color="red" size="medium" fullWidth onClick={onCancel}>
            <FormattedMessage {...glossary.cancel} />
          </Button>
          <Button color="primary" size="medium" fullWidth onClick={onContinue}>
            <FormattedMessage {...glossary.continue} />
          </Button>
        </Buttons>
      </Body>
    </Dialog>
  );
};

WarningDialog.fragments = {
  anyCard: gql`
    fragment WarningDialog_anyCard on AnyCardInterface {
      slug
      openedSo5Lineups {
        id
        so5Fixture {
          slug
        }
        so5Leaderboard {
          slug
          ...DivisionLogo_so5Leaderboard
        }
        ...formatLineupDisplayName_so5Lineup
      }
      sentInLiveOffers {
        id
        type
        endDate
      }
      ...WarningRow_anyCard
      ...isMyCardListedOnMarket_anyCard
      ...isSentInDirectOffer_anyCard
    }
    ${WarningRow.fragments.anyCard}
    ${isMyCardListedOnMarket.fragments.anyCard}
    ${isSentInDirectOffer.fragments.anyCard}
    ${formatLineupDisplayName.fragments.so5Lineup}
    ${DivisionLogo.fragments.so5Leaderboard}
  ` as TypedDocumentNode<WarningDialog_anyCard>,
};
